import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Title, Text, Card, Group,
  Button, ActionIcon,
  Skeleton, Tooltip, Badge, ScrollArea, Box,
} from '@mantine/core';
import { IconPlus, IconRefresh, IconSearch, IconEye } from '@tabler/icons-react';
import { Flex, Image, Table, TextInput } from '@/components';
import { toast } from '@/components/ui/providers/AlertProvider';
import { useACCourseService, ACCourseModelView } from '@actschurch/ccms-sdk';
import AddCourseDrawer from './Forms/AddCourseDrawer';
import { EmptyState } from '../../Information/Components/EmptyState';
import { useSidebarCountsContext } from '@/contexts/SidebarCountsContext';

// Pick readable text colour (black/white) against an arbitrary hex background,
// so the status pill reads correctly regardless of the configured statusColor.
function getContrastText(hex?: string | null): string {
  if (!hex) return '#fff';
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return '#fff';
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#1A1B1E' : '#fff';
}

function StatusBadge({ course }: { course: ACCourseModelView }) {
  if (course.statusName) {
    return (
      <Badge
        variant="filled"
        style={{
          backgroundColor: course.statusColor ?? 'var(--mantine-color-gray-5)',
          color: getContrastText(course.statusColor),
        }}
      >
        {course.statusName}
      </Badge>
    );
  }
  return (
    <Badge color={course.isActive ? 'green' : 'red'} variant="light">
      {course.isActive ? 'Active' : 'Inactive'}
    </Badge>
  );
}

function ShimmerRow() {
  return (
    <Table.Tr>
      {Array.from({ length: 8 }).map((_, i) => (
        <Table.Td key={i}><Skeleton height={14} radius="sm" /></Table.Td>
      ))}
    </Table.Tr>
  );
}

export default function CoursesPage() {
  const navigate = useNavigate();
  const courseService = useACCourseService();
  const { refetch } = useSidebarCountsContext();

  const [courses, setCourses] = useState<ACCourseModelView[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // ─── Add drawer ─────────────────────────────────────────────────────────────
  // Editing and deleting moved to the course detail page (header button and Danger Zone tab),
  // so this page only creates and views.
  const [addOpen, setAddOpen] = useState(false);

  // ─── Fetch ────────────────────────────────────────────────────────────────────
  const fetchCourses = useCallback(async () => {
    setLoading(true);
    try {
      setCourses(await courseService.listView());
      refetch(); // Update sidebar counts after fetching courses
    } catch (e) {
      console.error(e);
      toast.error('Error', 'Failed to load courses.');
    } finally {
      setLoading(false);
    }
  }, [courseService, refetch]);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);


  // ─── Filtered list ────────────────────────────────────────────────────────────
  const filteredCourses = courses.filter((c) => {
    const term = searchTerm.toLowerCase();
    if (!term) return true;
    if (c.searchKeywords) return c.searchKeywords.toLowerCase().includes(term);
    return (
      (c.name ?? '').toLowerCase().includes(term) ||
      (c.description ?? '').toLowerCase().includes(term)
    );
  });

  // ─── Empty-state helpers ───────────────────────────────────────────────────────
  const hasNoCoursesAtAll = !loading && courses.length === 0;
  const hasNoFilteredResults = !loading && courses.length > 0 && filteredCourses.length === 0;

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <Container size="xl" py="xl">
      <Title order={2}>Courses</Title>
      <Text mb="lg">Manage Acts Care courses available to leaders and members.</Text>

      <Card withBorder radius="md" padding="lg" mb="md">
        <TextInput
          placeholder="Search by name or description"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.currentTarget.value)}
          leftSection={<IconSearch size={16} />}
          radius="md"
          styles={{ input: { width: '300px' } }}
          size="sm"
        />
      </Card>

      <AddCourseDrawer
        opened={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={fetchCourses}
      />

      {/* ── Table / Empty states ── */}
      <Card withBorder radius="md" padding="lg">
        <Flex justify="flex-end" gap="sm" mb="md">
          <Tooltip label="Refresh">
            <ActionIcon variant="subtle" onClick={fetchCourses} loading={loading} size="lg" >
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
          <Button leftSection={<IconPlus size={16} />} onClick={() => setAddOpen(true)}>
            Add Course
          </Button>
        </Flex>

        {hasNoCoursesAtAll ? (
          <EmptyState
            variant="no-data"
            entityLabel="courses"
            addLabel="Add Course"
            onAdd={() => setAddOpen(true)}
          />
        ) : hasNoFilteredResults ? (
          <EmptyState
            variant="no-results"
            entityLabel="courses"
            onClearFilters={() => setSearchTerm('')}
          />
        ) : (
          <ScrollArea offsetScrollbars>
            <Table tableVariant="hoverable">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Course Name</Table.Th>
                  <Table.Th>Course Image</Table.Th>
                  <Table.Th>Color</Table.Th>
                  <Table.Th>Description</Table.Th>
                  <Table.Th style={{ textAlign: 'center' }}>Sessions</Table.Th>
                  <Table.Th style={{ textAlign: 'center' }}>Files</Table.Th>
                  <Table.Th style={{ textAlign: 'center' }}>Registrations</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {loading
                  ? Array.from({ length: 5 }).map((_, i) => <ShimmerRow key={i} />)
                  : filteredCourses.map((course) => (
                      <Table.Tr key={course.courseID}>
                        <Table.Td fw={600}>{course.displayName ?? course.name ?? '—'}</Table.Td>
                        <Table.Td>
                          {course.imageURL ? (
                            <Image
                              src={course.imageURL }
                              style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                            />
                          ) : 
                          <Image
                              src={'/Course-placeholder.png' }
                              style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                            />
                          }
                        </Table.Td>
                        <Table.Td>
                          {course.color ? (
                            <Group gap={6}>
                              <Box
                                w={18} h={18}
                                style={{ borderRadius: 4, backgroundColor: course.color, border: '1px solid rgba(255,255,255,0.15)', flexShrink: 0 }}
                              />
                              <Text size="sm">{course.color}</Text>
                            </Group>
                          ) : '—'}
                        </Table.Td>

                        <Table.Td>
                          <Text size="sm" lineClamp={1} style={{ maxWidth: 260 }}>
                            {course.description ?? '—'}
                          </Text>
                        </Table.Td>

                        <Table.Td style={{ textAlign: 'center' }}>
                          <Text size="sm" fw={500}>{course.sessionCount ?? 0}</Text>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'center' }}>
                          <Text size="sm" fw={500}>{course.totalFiles ?? 0}</Text>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'center' }}>
                          <Badge variant="light" color="blue">
                            {course.activeRegistrationsCount ?? 0}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <StatusBadge course={course} />
                        </Table.Td>
                        <Table.Td>
                          {/* View only. Editing lives on the course detail page header and
                              deleting in its Danger Zone tab, so both act on a course you are
                              actually looking at. */}
                          <Group gap={4}>
                            <ActionIcon variant="light" onClick={() => navigate(`/Grow/Courses/CourseDetails/${course.courseID}`)}>
                              <IconEye size={20} color="blue" />
                            </ActionIcon>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Card>
    </Container>
  );
}